import { useCallback, useState } from "react";
import {
  BrowserPlan,
  ActionStatus,
  ACTION_IDS,
  requestFormatResponse,
} from "../api-services/ai-chat";
import { ChatMessage } from "./useBackofficeChat";
import { getContactList } from "../api-services/contact";
import { listLoans } from "../api-services/loan-crm";
import {
  BROWSER_PLANS_ERROR_MESSAGES,
  API_DEFAULTS,
  UI_CONSTANTS,
  ACTION_STATUS,
} from "../commons/ai-chat.constants";

interface UsePlanExecutorProps {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  tenantId?: string | null;
  agencyId?: string;
  conversationID?: string | null;
}

export const usePlanExecutor = ({
  messages,
  setMessages,
  tenantId,
  agencyId,
  conversationID,
}: UsePlanExecutorProps) => {
  // Structure: Record<messageIndex, Record<planId, Record<actionId, ActionStatus>>>
  const [actionStatuses, setActionStatuses] = useState<
    Record<number, Record<string, Record<string, ActionStatus>>>
  >({});
  const [executingPlans, setExecutingPlans] = useState<Set<string>>(new Set());

  const setStatus = useCallback(
    (
      messageIndex: number,
      planId: string,
      actionId: string,
      status: ActionStatus
    ) => {
      setActionStatuses((prev) => ({
        ...prev,
        [messageIndex]: {
          ...prev[messageIndex],
          [planId]: {
            ...(prev[messageIndex]?.[planId] || {}),
            [actionId]: status,
          },
        },
      }));
    },
    []
  );

  const resetPlanExecutor = useCallback(() => {
    setActionStatuses({});
    setExecutingPlans(new Set());
  }, []);

  const executeSearchUserAction = useCallback(async (keyword: string) => {
    try {
      const response = await getContactList(
        API_DEFAULTS.SEARCH_USER_LIMIT,
        API_DEFAULTS.SEARCH_USER_PAGE,
        { q: keyword }
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : BROWSER_PLANS_ERROR_MESSAGES.UNKNOWN_ERROR,
      };
    }
  }, []);

  const executeSearchLoanAction = useCallback(
    async (keyword: string, loanId?: string) => {
      try {
        const response = await listLoans({
          keyword,
          limit: API_DEFAULTS.SEARCH_LOAN_LIMIT,
          page: API_DEFAULTS.SEARCH_LOAN_PAGE,
        });
        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : BROWSER_PLANS_ERROR_MESSAGES.UNKNOWN_ERROR,
        };
      }
    },
    []
  );

  const executeReadLoanApplicationAction = useCallback(
    async (loanId: string) => {
      try {
        const response = await listLoans({
          keyword: loanId,
          limit: API_DEFAULTS.READ_LOAN_LIMIT,
          page: API_DEFAULTS.READ_LOAN_PAGE,
        });
        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : BROWSER_PLANS_ERROR_MESSAGES.UNKNOWN_ERROR,
        };
      }
    },
    []
  );

  const executeFormatResponseAction = useCallback(
    async (
      planId: string,
      chatHistory: ChatMessage[],
      actionResults: Record<string, any>
    ) => {
      if (!tenantId) {
        return {
          success: false,
          error: BROWSER_PLANS_ERROR_MESSAGES.TENANT_ID_REQUIRED,
        };
      }

      try {
        // Filter out messages with browserPlans from history
        const historyForApi = chatHistory
          .filter((m) => {
            if (
              m.role === "assistant" &&
              m.browserPlans &&
              m.browserPlans.length > 0
            ) {
              return false;
            }
            return m.role === "user" || m.role === "assistant";
          })
          .map((m) => ({ role: m.role, content: m.content }));

        const response = await requestFormatResponse(
          {
            planId,
            chatHistory: historyForApi,
            actionResults,
            conversationID: conversationID ?? undefined,
          },
          agencyId
        );

        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : BROWSER_PLANS_ERROR_MESSAGES.UNKNOWN_ERROR,
        };
      }
    },
    [tenantId, agencyId, conversationID]
  );

  const handlePlanClick = useCallback(
    async (plan: BrowserPlan, messageIndex: number) => {
      if (!plan.actions || plan.actions.length === 0) {
        return;
      }

      // Prevent execution if already executing
      if (executingPlans.has(plan.id)) {
        return;
      }

      // Mark plan as executing
      setExecutingPlans((prev) => new Set(prev).add(plan.id));

      // Initialize all action statuses as pending
      const initialStatuses: Record<string, ActionStatus> = {};
      plan.actions.forEach((action) => {
        initialStatuses[action.id] = ACTION_STATUS.PENDING;
      });
      setActionStatuses((prev) => ({
        ...prev,
        [messageIndex]: {
          ...prev[messageIndex],
          [plan.id]: initialStatuses,
        },
      }));

      const actionResults: Record<string, any> = {};

      // Filter out format_response action (handled separately)
      const actionsToExecute = plan.actions.filter(
        (action) => action.id !== ACTION_IDS.FORMAT_RESPONSE
      );

      try {
        // Execute actions sequentially using reduce
        await actionsToExecute.reduce(async (previousPromise, action) => {
          await previousPromise;

          setStatus(messageIndex, plan.id, action.id, ACTION_STATUS.EXECUTING);

          try {
            let result;
            const keyword = action.parameters?.keyword || "";

            if (action.id === ACTION_IDS.SEARCH_USER) {
              result = await executeSearchUserAction(keyword);
            } else if (action.id === ACTION_IDS.SEARCH_LOAN) {
              result = await executeSearchLoanAction(keyword);
            } else if (action.id === ACTION_IDS.READ_LOAN_APPLICATION) {
              const loanId = action.parameters?.loanId || keyword;
              result = await executeReadLoanApplicationAction(loanId);
            } else {
              result = {
                success: false,
                error: BROWSER_PLANS_ERROR_MESSAGES.UNKNOWN_ACTION(action.id),
              };
            }

            if (result.success) {
              setStatus(
                messageIndex,
                plan.id,
                action.id,
                ACTION_STATUS.COMPLETED
              );
              actionResults[action.id] = result.data;
            } else {
              setStatus(messageIndex, plan.id, action.id, ACTION_STATUS.FAILED);
              actionResults[action.id] = { error: result.error };
            }
          } catch (error) {
            setStatus(messageIndex, plan.id, action.id, ACTION_STATUS.FAILED);
            actionResults[action.id] = {
              error:
                error instanceof Error
                  ? error.message
                  : BROWSER_PLANS_ERROR_MESSAGES.UNKNOWN_ERROR,
            };
            // Re-throw to halt further action execution on unexpected exceptions
            throw error;
          }
        }, Promise.resolve());

        // Handle format_response if it exists
        const formatResponseAction = plan.actions.find(
          (a) => a.id === ACTION_IDS.FORMAT_RESPONSE
        );

        if (formatResponseAction) {
          setStatus(
            messageIndex,
            plan.id,
            formatResponseAction.id,
            ACTION_STATUS.EXECUTING
          );

          const result = await executeFormatResponseAction(
            plan.id,
            messages,
            actionResults
          );

          if (result.success && result.data) {
            setStatus(
              messageIndex,
              plan.id,
              formatResponseAction.id,
              ACTION_STATUS.COMPLETED
            );
            const raw = result.data as {
              status?: string;
              message?: string;
              data?: {
                response?: Array<{ content: string }>;
              };
              response?: Array<{ content: string }>;
            };
            const segments =
              raw.response ||
              raw.data?.response ||
              ([] as Array<{ content: string }>);

            const formattedContent =
              segments.map((seg) => seg.content).join(UI_CONSTANTS.CONTENT_SEPARATOR) ||
              "";

            if (formattedContent) {
              setMessages((prev) => [
                ...prev,
                {
                  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                  role: "assistant",
                  content: formattedContent,
                  isStreaming: false,
                },
              ]);
            }
          } else {
            setStatus(
              messageIndex,
              plan.id,
              formatResponseAction.id,
              ACTION_STATUS.FAILED
            );
          }
        }
      } catch (error) {
        // Plan execution failed or halted
      } finally {
        // Mark plan as completed
        setExecutingPlans((prev) => {
          const next = new Set(prev);
          next.delete(plan.id);
          return next;
        });
      }
    },
    [
      executingPlans,
      setStatus,
      executeSearchUserAction,
      executeSearchLoanAction,
      executeReadLoanApplicationAction,
      executeFormatResponseAction,
      messages,
      setMessages,
    ]
  );

  return {
    actionStatuses,
    executingPlans,
    handlePlanClick,
    resetPlanExecutor,
  };
};
